import { Grid } from '@mui/material';
import { CancellationNonResidentFormCrumbs } from 'cancellations/constants/cancellation-non-resident-crumbs';
import { cancellationValidationSchema } from './validation-schema';
import {
  transformAnprResponse,
  transformAnprRequestDeath,
} from 'cancellations/utils/transform-anpr-response-non-resident';
import { Form, Formik } from 'formik';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprSaveData, SubjectData, SubjectsList } from 'shell/models/person';
import { ItemVM } from 'shell/view-models/item';
import PmiTitle from 'shell/components/shared/title';
import { CancellationNonResident } from 'cancellations/view-models/cancellation-non-resident';
import CancellationNonResidentContent from 'cancellations/components/cancellation-non-resident';
import CancellationNonResidentStepButtons from 'cancellations/components/cancellation-non-resident/buttons';
import { CodeStepButtonsCancellation } from 'cancellations/constants/code-step-buttons-cancellation';

interface RegistrationDeathNonResidentPageProps {
  isWithoutBreadcrumbs?: boolean;
  onHandleExit?: () => void;
  onConfirm?: () => void;
  initialValues?: CancellationNonResident;
  disabledFromAct?: boolean;
  goBackUrl?: string;
  editSipoUrl?: string;
}

const CancellationNonResidentPage = ({
  isWithoutBreadcrumbs = false,
  disabledFromAct = false,
  onConfirm,
  initialValues = { activeStep: CodeStepButtonsCancellation.datiCancellazione },
  goBackUrl = '/cancellazioni/non-residenti',
}: RegistrationDeathNonResidentPageProps): React.ReactElement => {
  const [queryParams] = useSearchParams();
  const navigate = useNavigate();

  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';
  const idIntestatario = queryParams.get('idIntestatario') || '';

  const [anprResponse] = useHttpFetch<HttpResponse<SubjectsList>, SubjectData[]>(
    `${__PERSON_API_URL__}/non-residenti/soggetto`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          matricolaSipo: idIntestatario || undefined,
          idSchedaSoggettoAnpr: idSchedaSoggettoANPR || undefined,
        },
      },
      transformer: (response) => response.data.payload.datiSoggetto,
    }
  );

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [responseCancellation, saveCancellation] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/non-residenti/cancellazione-morte`, {
    onBootstrap: false,
    transformer: transformResponseSaveDataAnpr,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
  });

  const castedAnprData = responseCancellation.data as SaveDataAnpr;

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (
      castedAnprData?.errors?.length &&
      castedAnprData?.errors?.length > 0 &&
      visibility.error === undefined
    ) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  const handleSubmit = (form: CancellationNonResident) => {
    const data = transformAnprRequestDeath(
      form,
      (anprResponse.data as SubjectData[])[0],
      fiscalCode
    );

    (
      saveCancellation({
        data,
      }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
    ).then((response) => {
      const castedResponse = response as SaveDataAnpr;

      setVisibility({ error: undefined, warning: undefined });

      if (castedResponse?.errors?.length) {
        window.scrollTo(0, 0);
        return;
      }

      if (onConfirm) {
        onConfirm();
        return;
      }

      toast.success(`Cancellazione di ${form.generalityForm?.fiscalCode} eseguita con successo`);

      navigate(goBackUrl);
    });
  };

  const initialForm: CancellationNonResident | undefined = useMemo(() => {
    if (!anprResponse.data || anprResponse.isLoading || anprResponse.pristine) {
      return undefined;
    }
    return transformAnprResponse(anprResponse.data as SubjectData[]);
  }, [anprResponse.data, anprResponse.isLoading, anprResponse.pristine]);

  if (!initialForm) {
    return <></>;
  }

  return (
    <Fragment>
      {isWithoutBreadcrumbs ? undefined : <Breadcrumbs items={CancellationNonResidentFormCrumbs} />}
      <PmiTitle title="Cancellazione per morte non residente" paddingBottom={1} />
      <div className="pmi-mt-2">
        <Formik
          initialValues={{ ...(initialForm || {}), ...initialValues }}
          onSubmit={handleSubmit}
          validationSchema={cancellationValidationSchema}
        >
          <Form>
            <Grid container spacing={1}>
              <Grid item xs={12} md={2}>
                <CancellationNonResidentStepButtons onCancel={() => navigate(goBackUrl)} />
              </Grid>
              <Grid item xs={12} md={10}>
                <div className="pmi-px-md-4">
                  <SearchMessages
                    type={MessageType.Error}
                    title={errorMessage.title || ''}
                    messages={errorMessage.messages || []}
                    visibility={errorMessage.visibility || false}
                    onChangeVisibility={onVisibility}
                  />
                  <SearchMessages
                    type={MessageType.Warning}
                    title={
                      castedAnprData?.warnings?.length === 1
                        ? `C'è 1 warning`
                        : `Ci sono ${castedAnprData?.warnings?.length} warning`
                    }
                    messages={castedAnprData?.warnings || []}
                    visibility={
                      visibility.warning !== undefined
                        ? visibility.warning
                        : (castedAnprData?.warnings || [])?.length > 0
                    }
                    onChangeVisibility={onVisibility}
                  />
                  <CancellationNonResidentContent disabled={disabledFromAct} />
                </div>
              </Grid>
            </Grid>
          </Form>
        </Formik>
      </div>
    </Fragment>
  );
};

CancellationNonResidentPage.displayName = 'Cancellation non resident page component';

export default CancellationNonResidentPage;
